/* eslint-disable react-hooks/exhaustive-deps */
import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    IconButton,
    makeStyles,
    MenuItem,
    Select,
} from "@material-ui/core";
import { useEffect, useMemo, useState } from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { translateAbbreviatedDayToSpanish } from "../OrganizationSettings/helpers";

const useStyles = makeStyles(theme => ({
    copyButton: {
        cursor: "pointer",
        "&:hover": {
            transform: "scale(1.1)",
            transition: "transform 0.3s ease-in-out",
        },
    },
    textPrimary: {
        color: theme.palette.primary.main,
    },
}));

export default function TimeSlotSelector({
    timeSlots,
    disabledTimeSlot,
    handleSetTimeSlots,
    workHours,
    nonWorkHours,
}) {
    const classes = useStyles();

    // 00..23 para el Select (solo hora)
    const HOURS = useMemo(
        () => Array.from({ length: 24 }, (_, h) => String(h).padStart(2, "0")),
        []
    );

    // Convierte "7" | "07" | "07:30" | "07:00" -> "07"
    const normalizeToHH = (value) => {
        if (value === null || value === undefined) return "00";
        const raw = String(value).trim();
        const hPart = raw.includes(":") ? raw.split(":")[0] : raw;
        const num = Number(hPart);

        if (Number.isNaN(num)) return "00";
        if (num < 0) return "00";
        if (num > 23) return "23";
        return String(num).padStart(2, "0");
    };

    // Convierte cualquier cosa a "HH:00"
    const normalizeToHH00 = (value) => `${normalizeToHH(value)}:00`;

    // Para mostrar el Select desde el state "HH:MM"
    const hhmmToHH = (hhmm) => normalizeToHH(hhmm);

    const [selectedDays, setSelectedDays] = useState([]);

    // ✅ STATE SIGUE SIENDO "HH:MM" (como tu implementación actual)
    const [selectedHoursTimeSlot, setHoursTimeSlot] = useState({
        time_from: "00:00",
        time_to: "23:59", // si querés exacto a hora, ponelo "23:00"
    });

    // Scroll del menú
    const menuProps = useMemo(
        () => ({
            PaperProps: {
                style: {
                    maxHeight: 260,
                    overflowY: "auto",
                },
            },
            anchorOrigin: { vertical: "bottom", horizontal: "left" },
            transformOrigin: { vertical: "top", horizontal: "left" },
            getContentAnchorEl: null, // MUI v4
        }),
        []
    );

    useEffect(() => {
        getTimeSlotsValues();
    }, []);

    useEffect(() => {
        const timeslots = `${selectedDays}=${selectedHoursTimeSlot?.time_from}a${selectedHoursTimeSlot?.time_to}`;
        handleSetTimeSlots(disabledTimeSlot ? "" : timeslots);
    }, [selectedDays, selectedHoursTimeSlot, disabledTimeSlot]);

    const getTimeSlotsValues = () => {
        if (timeSlots?.length > 0) {
            const timeslotsAux = timeSlots;

            const elements = timeslotsAux.split("=")[0];
            const days = elements.split(",");
            setSelectedDays(days);

            const hours = timeslotsAux.split("=")[1];
            const [time_from_raw, time_to_raw] = hours.split("a");

            // ✅ normalizo a HH:00 siempre
            const time_from = normalizeToHH00(time_from_raw);
            const time_to = normalizeToHH00(time_to_raw);

            setHoursTimeSlot(prev => ({
                ...prev,
                time_from,
                time_to,
            }));
        }
    };

    const handleTimeSlot = (event) => {
        const { value, checked } = event.target;
        if (checked) setSelectedDays([...selectedDays, value]);
        else setSelectedDays(selectedDays.filter(day => day !== value));
    };

    // ✅ handler del Select: recibe "HH" y lo guarda como "HH:00"
    const handleHoursTimeSlot = (event) => {
        const { value, name } = event.target; // value = "00".."23"
        const normalizedHHMM = normalizeToHH00(value);

        setHoursTimeSlot(prev => ({
            ...prev,
            [name]: normalizedHHMM,
        }));
    };

    const handleCopyWorkHours = (elem) => {
        setSelectedDays(elem?.days || []);

        const from = normalizeToHH00(elem?.hours?.[0]?.from);
        const to = normalizeToHH00(elem?.hours?.[0]?.to || elem?.hours?.[0]?.start);

        setHoursTimeSlot(prev => ({
            ...prev,
            time_from: from,
            time_to: to,
        }));
    };

    return (
        <Box>
            {(workHours?.length > 0 || nonWorkHours?.length > 0) && (
                <Box style={{ fontSize: "16px", marginBottom: "5px" }}>
                    Franjas horarias preestablecidas
                </Box>
            )}

            {workHours?.length > 0 && (
                <Box>
                    <Box style={{ fontSize: "16px", marginBottom: "5px" }} className={classes.textPrimary}>
                        Laborables
                    </Box>

                    {workHours.map((elem, index) => (
                        <Box key={index} display="flex" alignItems="center" gridGap={5} marginBottom={1}>
                            <IconButton disabled={disabledTimeSlot}>
                                <ContentCopyIcon
                                    disabled={disabledTimeSlot}
                                    className={classes.copyButton}
                                    style={{ fontSize: "18px" }}
                                    onClick={() => handleCopyWorkHours(elem)}
                                />
                            </IconButton>

                            {elem?.days?.map(day => (
                                <span key={day}>{translateAbbreviatedDayToSpanish(day)}</span>
                            ))}

                            ({elem?.hours?.[0]?.from}-{elem?.hours?.[0]?.to})
                        </Box>
                    ))}
                </Box>
            )}

            {nonWorkHours?.length > 0 && (
                <Box>
                    <Box style={{ fontSize: "16px", marginBottom: "5px" }} className={classes.textPrimary}>
                        No laborables
                    </Box>

                    {nonWorkHours.map((elem, index) => (
                        <Box key={index} display="flex" alignItems="center" gridGap={5} marginBottom={1}>
                            <IconButton disabled={disabledTimeSlot}>
                                <ContentCopyIcon
                                    className={classes.copyButton}
                                    style={{ fontSize: "18px" }}
                                    onClick={() => handleCopyWorkHours(elem)}
                                />
                            </IconButton>

                            {elem?.days?.map(day => (
                                <span key={day}>{translateAbbreviatedDayToSpanish(day)}</span>
                            ))}

                            ({elem?.hours?.[0]?.from}-{elem?.hours?.[0]?.to || elem?.hours?.[0]?.start})
                        </Box>
                    ))}
                </Box>
            )}

            {/* DÍAS */}
            <Box>
                <FormControl disabled={disabledTimeSlot}>
                    <FormGroup row>
                        {["lu", "ma", "mi", "ju", "vi", "sa", "do"].map(elem => (
                            <FormControlLabel
                                style={{ marginRight: "0px", marginLeft: "0px" }}
                                key={elem}
                                control={
                                    <Checkbox
                                        value={elem}
                                        checked={selectedDays.includes(elem)}
                                        onChange={handleTimeSlot}
                                        color="primary"
                                    />
                                }
                                label={elem}
                                labelPlacement="start"
                            />
                        ))}
                    </FormGroup>
                </FormControl>
            </Box>

            {/* HORAS */}
            <Box mt={1} display="flex" alignItems="center">
                {/* DESDE */}
                <Box>
                    <Box style={{ fontSize: 12, marginBottom: 4 }}>Desde:</Box>
                    <FormControl variant="outlined" size="small" disabled={disabledTimeSlot}>
                        <Select
                            name="time_from"
                            value={hhmmToHH(selectedHoursTimeSlot.time_from)} // ✅ muestra "HH"
                            onChange={handleHoursTimeSlot}                   // ✅ guarda "HH:00"
                            MenuProps={menuProps}                             // ✅ scroll
                        >
                            {HOURS.map(h => (
                                <MenuItem key={h} value={h}>
                                    {`${h} hs`}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {/* HASTA */}
                <Box ml={2}>
                    <Box style={{ fontSize: 12, marginBottom: 4 }}>Hasta:</Box>
                    <FormControl variant="outlined" size="small" disabled={disabledTimeSlot}>
                        <Select
                            name="time_to"
                            value={hhmmToHH(selectedHoursTimeSlot.time_to)}
                            onChange={handleHoursTimeSlot}
                            MenuProps={menuProps}
                        >
                            {HOURS.map(h => (
                                <MenuItem key={h} value={h}>
                                    {`${h} hs`}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Box>
        </Box>
    );
}
